import { PATH } from 'app/constants/path';
import { SocketClient } from 'app/hooks/socket';

import { getAuth, removeAuth, setAuth } from '../modules/auth';
import { refresh as refreshRequest } from '../modules/auth/core/requests';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

// Must match CSRF_COOKIE / CSRF_HEADER on the API (settings.constants.ts).
const CSRF_COOKIE = 'sims_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

const isAuthCookieEndpoint = (url = '') =>
  url.includes('/auth/refresh') || url.includes('/auth/logout');

const readCookie = (name: string): string | undefined =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

// De-dupe concurrent refreshes: many requests can 401 at once, but only one refresh should
// actually hit the server; the rest await the same in-flight promise.
let refreshPromise: Promise<string | undefined> | null = null;

const silentRefresh = (): Promise<string | undefined> => {
  if (!refreshPromise) {
    refreshPromise = refreshRequest()
      .then(({ data }) => {
        const token = data?.data?.access_token;
        if (token) {
          setAuth({ access_token: token });
          // Force the socket to re-handshake with the refreshed token instead of lingering
          // on the one it originally connected with, which may be about to expire.
          SocketClient.socket.disconnect();
          SocketClient.socket.connect();
        }
        return token;
      })
      .catch(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export default function () {
  axios.defaults.headers['Accept'] = 'application/json';
  // Send the httpOnly refresh + csrf cookies on cross-site requests.
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use((config) => {
    const auth = getAuth();
    if (config.headers && auth?.access_token) {
      config.headers.Authorization = `Bearer ${auth.access_token}`;
    }

    // Double-submit CSRF: echo the JS-readable csrf cookie back in a header on the
    // cookie-authenticated auth endpoints.
    if (config.headers && isAuthCookieEndpoint(config.url)) {
      const csrf = readCookie(CSRF_COOKIE);
      if (csrf) {
        config.headers[CSRF_HEADER] = csrf;
      }
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const original = error.config as
        | (AxiosRequestConfig & { _retry?: boolean })
        | undefined;
      const url = original?.url || '';
      const isLoginOrRefresh =
        url.includes('/auth/refresh') || url.includes('/auth/login');

      // Access token likely expired: attempt one silent refresh, then replay the request.
      if (status === 401 && original && !original._retry && !isLoginOrRefresh) {
        original._retry = true;
        const token = await silentRefresh();
        if (token) {
          original.headers = {
            ...(original.headers || {}),
            Authorization: `Bearer ${token}`,
          };
          return axios(original);
        }
      }

      if (window.location.pathname !== PATH.LOGIN && (status === 401 || status === 403)) {
        removeAuth();
        window.location.href = PATH.LOGIN;
      }

      return Promise.reject(error);
    },
  );
}
